/**
 * Validation Status Badge Component
 * @implements FS-005.8.3 - Representation (Derived Values)
 * @implements US-037 - Basic Roadmap Viewing (Validation status for consolidation items)
 *
 * Displays the validation status of an epic during consolidation.
 * Only shown when the epic is in the consolidation column.
 */

import { Chip } from '@mui/material';
import {
  HourglassEmpty as PendingIcon,
  CheckCircle as ValidatedIcon,
  Warning as IssuesIcon,
} from '@mui/icons-material';

/**
 * Validation status values matching the backend enum
 * @implements FS-005.8.3 - Representation (Derived Values)
 */
export type ValidationStatus = 'pending' | 'validated' | 'issues';

/**
 * Props for ValidationStatusBadge component
 */
export interface ValidationStatusBadgeProps {
  /** The validation status to display */
  status: ValidationStatus | null | undefined;
  /** Size of the badge */
  size?: 'small' | 'medium';
}

/**
 * ValidationStatusBadge - Shows validation status during consolidation
 *
 * @implements FS-005.8.3 - Representation (Derived Values)
 * @implements US-037 - Validation status shown for items in consolidation
 *
 * Features:
 * - Pending: orange/warning with hourglass icon
 * - Validated: green/success with check icon
 * - Issues: red/error with warning icon
 * - Returns null when status is not available
 */
export const ValidationStatusBadge = ({
  status,
  size = 'small',
}: ValidationStatusBadgeProps) => {
  if (!status) {
    return null;
  }

  switch (status) {
    case 'pending':
      return (
        <Chip
          label="Pending Validation"
          size={size}
          color="warning"
          variant="outlined"
          icon={<PendingIcon />}
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
      );
    case 'validated':
      return (
        <Chip
          label="Validated"
          size={size}
          color="success"
          variant="filled"
          icon={<ValidatedIcon />}
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
      );
    case 'issues':
      return (
        <Chip
          label="Issues Found"
          size={size}
          color="error"
          variant="outlined"
          icon={<IssuesIcon />}
          sx={{ fontWeight: 500, fontSize: '0.75rem' }}
        />
      );
    default:
      return null;
  }
};
